/**
 * ═══════════════════════════════════════════════════════════════════════════
 * EL TELÉFONO — QUE NO PASE UN NÚMERO FALSO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ── POR QUÉ EXISTE ──────────────────────────────────────────────────────
 *
 * 4-oct-2026, el fundador probando el registro de negocio: «el número de
 * teléfono también pasa aunque ese número sea falso». La validación entera era
 * `if (!formData.phone)` — o sea «no está vacío». Pasaban `1`, `aaa`, `hola` y
 * `000-000-0000`.
 *
 * El `keyboardType="phone-pad"` de la pantalla NO es una validación: es el
 * teclado que se muestra. No filtra lo que se escribe ni lo que se pega.
 *
 * ── POR QUÉ ESTÁ AQUÍ Y NO SUELTO EN LA PANTALLA ────────────────────────
 *
 * Porque el mismo criterio ya vivía copiado en dos sitios más, cada uno con
 * un número distinto, y eso acaba en que la app acepta un teléfono que el
 * servidor rechaza:
 *
 *   · src/app/add-phone.tsx  → 10 a 15 dígitos
 *   · functions/src/orders.ts → 7 a 15 dígitos
 *
 * Aquí se usa el criterio de `add-phone` (10 a 15), que es el más estricto de
 * los dos, para que lo que pase por esta puerta pase seguro por las otras.
 * Un número de 7 dígitos lo acepta el servidor pero no esta pantalla: eso es
 * fallar del lado seguro.
 *
 * ── LO QUE NO HACE, Y HAY QUE SABERLO ───────────────────────────────────
 *
 * Esto NO comprueba que el teléfono exista ni que sea de esa persona: para eso
 * hace falta mandar un SMS. Lo que hace es tumbar lo que claramente no es un
 * teléfono. Para el número de contacto de un negocio —que lo llama un cliente,
 * no la app— eso es lo proporcionado.
 */

/** Los números de relleno que no llama nadie. */
const RELLENOS = new Set([
  '0000000000',
  '1111111111',
  '1234567890',
  '0123456789',
  '9999999999',
  '8090000000',
  '00000000000',
  '11111111111',
]);

export type ResultadoDeTelefono =
  | { ok: true; digitos: string }
  | { ok: false; motivo: string };

/**
 * ⚠️ PARA QUIÉN ES EL NÚMERO.
 *
 * Este archivo nació para el registro de un NEGOCIO, y sus motivos hablaban de
 * «tus clientes» y «el teléfono del negocio». Luego lo empezaron a usar
 * `add-phone.tsx` y `edit-profile.tsx`, que son las pantallas del PASAJERO: a
 * alguien que solo quiere pedir un viaje se le estaba diciendo que escribiera
 * «el teléfono al que te pueden llamar tus clientes». Un texto así confunde y
 * hace dudar de si uno está en la pantalla correcta.
 *
 * Se resuelve con un parámetro y no aplanando los textos: la frase del negocio
 * es buena para el negocio, y la neutra es la correcta para todo lo demás. El
 * valor por defecto es el personal, que es el caso más común.
 */
export type ParaQuien = 'negocio' | 'personal';

/** Solo los dígitos. Se queda con el `+` fuera: aquí se compara forma, no país. */
export function soloDigitosDeTelefono(valor: unknown): string {
  return String(valor ?? '').replace(/\D/g, '');
}

/**
 * Comprueba que esto tenga forma de teléfono.
 *
 * El motivo va en español y dirigido a la persona: es el texto que se le
 * enseña tal cual.
 */
export function validarTelefono(
  valor: unknown,
  paraQuien: ParaQuien = 'personal'
): ResultadoDeTelefono {
  const digitos = soloDigitosDeTelefono(valor);
  const esNegocio = paraQuien === 'negocio';

  if (digitos.length === 0) {
    return {
      ok: false,
      motivo: esNegocio ? 'Escribe el teléfono del negocio.' : 'Escribe tu número de teléfono.',
    };
  }
  if (digitos.length < 10) {
    return {
      ok: false,
      motivo:
        `Un teléfono lleva al menos 10 números y escribiste ${digitos.length}. ` +
        'Incluye el código de área, por ejemplo 809-555-1234.',
    };
  }
  if (digitos.length > 15) {
    return { ok: false, motivo: 'Ese número tiene demasiados dígitos para ser un teléfono.' };
  }
  const noEsReal = esNegocio
    ? 'Ese número no es real. Escribe el teléfono al que te pueden llamar tus clientes.'
    : 'Ese número no es real. Escribe el teléfono al que te pueden llamar.';
  if (RELLENOS.has(digitos)) {
    return { ok: false, motivo: noEsReal };
  }
  // Todos los dígitos iguales (2222222222, 7777777777…): tampoco.
  if (/^(\d)\1+$/.test(digitos)) {
    return { ok: false, motivo: noEsReal };
  }
  return { ok: true, digitos };
}

/**
 * ── EL CORREO ───────────────────────────────────────────────────────────
 *
 * Va en este mismo archivo porque se valida en el mismo sitio y por el mismo
 * motivo, y separarlo en otro archivo de seis líneas no ayuda a nadie.
 *
 * ESTO NO ES COSMÉTICO. El correo del negocio tiene UN consumidor y es el que
 * decide si puede cobrar: `functions/src/admin.ts` se lo pasa a Stripe tal cual
 * al crear su cuenta (`email: rest.email ?? undefined`). Stripe rechaza un
 * correo malformado, y ese fallo se traga a propósito para no bloquear la
 * aprobación — o sea que el negocio queda aprobado, sin cuenta de Stripe, y
 * nadie se entera hasta que intenta cobrar.
 *
 * El regex es el MISMO que ya usa la pantalla de entrada (`src/app/auth.tsx`):
 * algo, arroba, algo, punto, algo. No valida que el buzón exista —eso solo lo
 * dice un correo de verdad— pero tumba `x`, `hola` y `pepe@`.
 */
const FORMA_DE_CORREO = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * ── EL DOMINIO MAL ESCRITO ──────────────────────────────────────────────
 *
 * `enmycabrera732@gmil.com` pasa el regex de arriba sin despeinarse: tiene
 * arroba, tiene punto, tiene letras. Es una direccion PERFECTAMENTE VALIDA de
 * un dominio que no existe. El correo de verificacion sale, se pierde, y la
 * persona se queda atrapada para siempre en la pantalla "Verifica tu correo"
 * sin entender por que.
 *
 * Paso de verdad el 15-oct-2026, con la campana de anuncios corriendo: o sea
 * que ese registro ya estaba pagado.
 *
 * NO se rechaza, se PREGUNTA. Alguien podria tener de verdad un correo en un
 * dominio raro, y bloquearlo por sospecha seria peor que el fallo. Lo que se
 * hace es avisar cuando el dominio esta a uno o dos errores de tecleo de uno
 * de los grandes: eso casi siempre es un dedo que resbalo, nunca una empresa.
 *
 * La lista es corta a proposito: solo los que de verdad usa la gente en RD.
 * Un dominio propio (tunegocio.com.do) esta lejos de todos y no dispara nada.
 */
const DOMINIOS_COMUNES = [
  'gmail.com', 'hotmail.com', 'outlook.com', 'yahoo.com', 'icloud.com',
  'live.com', 'hotmail.es', 'outlook.es', 'yahoo.es', 'me.com', 'aol.com',
  'protonmail.com', 'claro.net.do',
];

/** Cuantos cambios de una letra hacen falta para convertir `a` en `b`. */
function distancia(a: string, b: string): number {
  if (a === b) return 0;
  // Si se diferencian mucho en largo ya no vale la pena: no es un dedo que resbalo.
  if (Math.abs(a.length - b.length) > 2) return 99;
  let fila = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    const nueva = [i];
    for (let j = 1; j <= b.length; j++) {
      nueva[j] = Math.min(
        fila[j] + 1,
        nueva[j - 1] + 1,
        fila[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1)
      );
    }
    fila = nueva;
  }
  return fila[b.length];
}

/**
 * Si el dominio esta a uno o dos tecleos de uno grande, devuelve el bueno.
 * Si es exacto, o si esta lejos de todos, devuelve null y no se molesta a nadie.
 */
export function dominioParecido(correo: string): string | null {
  const dominio = correo.split('@')[1]?.toLowerCase().trim();
  if (!dominio || DOMINIOS_COMUNES.includes(dominio)) return null;
  let mejor: string | null = null;
  let mejorD = 99;
  for (const d of DOMINIOS_COMUNES) {
    const n = distancia(dominio, d);
    if (n < mejorD) { mejorD = n; mejor = d; }
  }
  // 1 o 2 cambios: un dedo que resbalo. De 3 en adelante ya es otro dominio.
  // Y la MISMA PRIMERA LETRA: al teclear mal rara vez se falla la primera. Sin
  // esta condicion, `a.com` sugeria `me.com` (dos cambios sobre algo tan corto
  // que ya no significa nada). Medido con una prueba antes de enchufarlo.
  return mejorD >= 1 && mejorD <= 2 && mejor && dominio[0] === mejor[0] ? mejor : null;
}

export type ResultadoDeCorreo =
  | { ok: true; correo: string; quisoDecir?: string }
  | { ok: false; motivo: string };

export function validarCorreo(
  valor: unknown,
  paraQuien: ParaQuien = 'personal'
): ResultadoDeCorreo {
  const correo = String(valor ?? '').trim().toLowerCase();
  if (!correo) {
    return {
      ok: false,
      motivo: paraQuien === 'negocio' ? 'Escribe el correo del negocio.' : 'Escribe tu correo.',
    };
  }
  if (correo.length > 120) {
    return { ok: false, motivo: 'Ese correo es demasiado largo.' };
  }
  if (!FORMA_DE_CORREO.test(correo)) {
    return {
      ok: false,
      motivo:
        'Ese correo no está bien escrito. Tiene que llevar arroba y un punto, ' +
        'por ejemplo contacto@tunegocio.com.',
    };
  }
  const bueno = dominioParecido(correo);
  return bueno ? { ok: true, correo, quisoDecir: correo.replace(/@.*$/, '@' + bueno) } : { ok: true, correo };
}
